/**
 * Live coaching tab.
 *
 * Reads/writes GET|PUT /api/admin/live-coaching-config:
 *  - enabled: master on/off switch for in-call nudges.
 *  - model + system prompt (reset-to-default → built-in DEFAULT_LIVE_SYSTEM_PROMPT).
 *  - cadence: max nudges/call, min gap (ms), nudge TTL (ms), eval floor (ms).
 *  - reference_cards: a full structured list editor (triggers / type / title /
 *    body rows; add / delete / reorder).
 *
 * NOTE: these cards use `type` (script|pricing|objection) — distinct from the
 * runtime /live `ReferenceCard` (which carries `category`). See types.ts.
 *
 * Also hosts the LIVE-CAPTURE controls (LiveCaptureSection — a different
 * endpoint, GET/PUT /api/admin/config): the billing kill-switch, the daily
 * spend-guard thresholds, and the capture-hours window — placed directly under
 * the nudge master switch so the "coaching vs capture" distinction is side by
 * side. HaltBanner (top of the tab) surfaces a fired auto-halt with one-click
 * re-enable.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { LiveReferenceCard } from "@/types";
import { Button, Card, SelectField } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import {
  ModelCard,
  SystemPromptCard,
  SavedNote,
  ErrorNote,
  NumberField,
  IconButton,
  moveItem,
} from "./shared";
import { HaltBanner, LiveCaptureSection } from "./LiveCaptureSection";

const LIVE_KEY = ["admin", "live-config"] as const;

const CARD_TYPE_OPTIONS: Array<{
  value: LiveReferenceCard["type"];
  label: string;
}> = [
  { value: "script", label: "Script" },
  { value: "pricing", label: "Pricing" },
  { value: "objection", label: "Objection" },
];

const BLANK_CARD = (): LiveReferenceCard => ({
  id: "",
  triggers: [],
  type: "script",
  title: "",
  body: "",
});

export function LiveCoachingTab() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: LIVE_KEY,
    queryFn: ({ signal }) => api.adminGetLiveConfig(signal),
  });

  const [enabled, setEnabled] = useState(true);
  const [guiV2Enabled, setGuiV2Enabled] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [systemPromptV2, setSystemPromptV2] = useState("");
  const [model, setModel] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [maxPerCall, setMaxPerCall] = useState(0);
  const [minGapMs, setMinGapMs] = useState(0);
  const [ttlMs, setTtlMs] = useState(0);
  const [evalFloorMs, setEvalFloorMs] = useState(0);
  const [cards, setCards] = useState<LiveReferenceCard[]>([]);
  // Per-card comma-separated trigger text (kept as a raw string while editing so
  // commas/spaces don't fight the parse on every keystroke).
  const [triggerText, setTriggerText] = useState<Record<number, string>>({});
  const [justSaved, setJustSaved] = useState(false);

  const data = query.data;
  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setGuiV2Enabled(data.gui_v2_enabled);
      setShowTranscript(data.show_transcript);
      setSystemPromptV2(data.system_prompt_v2);
      setModel(data.model);
      setSystemPrompt(data.system_prompt);
      setMaxPerCall(data.max_per_call);
      setMinGapMs(data.min_gap_ms);
      setTtlMs(data.ttl_ms);
      setEvalFloorMs(data.eval_floor_ms);
      setCards(data.reference_cards);
      setTriggerText(
        Object.fromEntries(
          data.reference_cards.map((c, i) => [i, c.triggers.join(", ")]),
        ),
      );
      setJustSaved(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (body: {
      enabled?: boolean;
      model?: string;
      system_prompt?: string;
      max_per_call?: number;
      min_gap_ms?: number;
      ttl_ms?: number;
      eval_floor_ms?: number;
      reference_cards?: LiveReferenceCard[];
      gui_v2_enabled?: boolean;
      system_prompt_v2?: string;
      show_transcript?: boolean;
    }) => api.adminSaveLiveConfig(body),
    onSuccess: () => {
      // PUT returns {ok,changed}; the GET refetch supplies the authoritative config.
      void qc.invalidateQueries({ queryKey: LIVE_KEY });
      setJustSaved(true);
    },
  });

  if (query.isLoading) {
    return <LoadingState label="Loading live-coaching settings…" />;
  }
  if (query.isError || !data) {
    return (
      <ErrorState error={query.error} onRetry={() => void query.refetch()} />
    );
  }

  const isCustom = data.system_prompt_is_custom;
  const cardsDirty =
    cards.length !== data.reference_cards.length ||
    cards.some((c, i) => {
      const o = data.reference_cards[i];
      return (
        !o ||
        c.id !== o.id ||
        c.type !== o.type ||
        c.title !== o.title ||
        c.body !== o.body ||
        c.triggers.length !== o.triggers.length ||
        c.triggers.some((t, ti) => t !== o.triggers[ti])
      );
    });
  const dirty =
    enabled !== data.enabled ||
    guiV2Enabled !== data.gui_v2_enabled ||
    showTranscript !== data.show_transcript ||
    systemPromptV2 !== data.system_prompt_v2 ||
    model !== data.model ||
    systemPrompt !== data.system_prompt ||
    maxPerCall !== data.max_per_call ||
    minGapMs !== data.min_gap_ms ||
    ttlMs !== data.ttl_ms ||
    evalFloorMs !== data.eval_floor_ms ||
    cardsDirty;
  const saving = save.isPending;

  function touch() {
    if (justSaved) setJustSaved(false);
  }

  // Parse the comma-separated draft for card i into the cards array.
  function parseTriggers(raw: string): string[] {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  function setCardTriggerText(i: number, raw: string) {
    touch();
    setTriggerText((prev) => ({ ...prev, [i]: raw }));
    setCards((rows) =>
      rows.map((r, idx) =>
        idx === i ? { ...r, triggers: parseTriggers(raw) } : r,
      ),
    );
  }
  function updateCard(i: number, patch: Partial<LiveReferenceCard>) {
    touch();
    setCards((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    );
  }
  function rebuildTriggerText(next: LiveReferenceCard[]) {
    setTriggerText(
      Object.fromEntries(next.map((c, i) => [i, c.triggers.join(", ")])),
    );
  }
  function removeCard(i: number) {
    touch();
    setCards((rows) => {
      const next = rows.filter((_, idx) => idx !== i);
      rebuildTriggerText(next);
      return next;
    });
  }
  function addCard() {
    touch();
    setCards((rows) => {
      const next = [...rows, BLANK_CARD()];
      rebuildTriggerText(next);
      return next;
    });
  }
  function reorderCard(from: number, to: number) {
    touch();
    setCards((rows) => {
      const next = moveItem(rows, from, to);
      rebuildTriggerText(next);
      return next;
    });
  }

  function saveAll() {
    setJustSaved(false);
    save.mutate({
      enabled,
      gui_v2_enabled: guiV2Enabled,
      show_transcript: showTranscript,
      system_prompt_v2: systemPromptV2,
      model,
      system_prompt: systemPrompt,
      max_per_call: maxPerCall,
      min_gap_ms: minGapMs,
      ttl_ms: ttlMs,
      eval_floor_ms: evalFloorMs,
      reference_cards: cards,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Auto-halt banner (spend guard fired / kill-switches off) — top of tab. */}
      <HaltBanner />

      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Live coaching</h2>
          <p className="text-sm text-muted-foreground">
            In-call nudges shown to the rep while a call is live.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SavedNote show={justSaved && !dirty} />
          <Button onClick={saveAll} disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Master switch (NUDGES ONLY — capture/billing is the section below) */}
      <Card className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Master switch — nudges only</h2>
          <p className="text-sm text-muted-foreground">
            Turn all in-call nudges on or off. This does NOT stop the capture or its
            billing — that&apos;s the &quot;Live capture&quot; switch below.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => {
            touch();
            setEnabled((v) => !v);
          }}
          className={
            "relative inline-flex h-6 w-11 items-center rounded-full transition " +
            (enabled ? "bg-primary" : "bg-muted")
          }
        >
          <span
            className={
              "inline-block h-4 w-4 transform rounded-full bg-background transition " +
              (enabled ? "translate-x-6" : "translate-x-1")
            }
          />
        </button>
      </Card>

      {/* GUI v2 live copilot — the context-aware panel (call card, stage strip,
          pricing, value points, objection helper). Ships dark; this flips it on
          for new calls. */}
      <Card className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">
              New live copilot (GUI v2)
              <span className="ml-2 align-middle text-xs font-normal text-muted-foreground">
                {guiV2Enabled ? "On" : "Off — using the classic panel"}
              </span>
            </h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              The context-aware panel: pinned call card, stage strip, deterministic
              vehicle pricing, ranked value points, objection helper, and the
              wrap-up scorecard. Applies to new calls (≤60s). Verify the pricing
              extract before turning this on for live calls.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={guiV2Enabled}
            onClick={() => {
              touch();
              setGuiV2Enabled((v) => !v);
            }}
            className={
              "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition " +
              (guiV2Enabled ? "bg-primary" : "bg-muted")
            }
          >
            <span
              className={
                "inline-block h-4 w-4 transform rounded-full bg-background transition " +
                (guiV2Enabled ? "translate-x-6" : "translate-x-1")
              }
            />
          </button>
        </div>
      </Card>

      {guiV2Enabled && (
        <Card className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Live transcript panel</h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Show the live transcript (rep / customer split) under the checklist in the
              copilot. Turn off if it&apos;s a distraction on the floor.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={showTranscript}
            onClick={() => {
              touch();
              setShowTranscript((v) => !v);
            }}
            className={
              "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition " +
              (showTranscript ? "bg-primary" : "bg-muted")
            }
          >
            <span
              className={
                "inline-block h-4 w-4 transform rounded-full bg-background transition " +
                (showTranscript ? "translate-x-6" : "translate-x-1")
              }
            />
          </button>
        </Card>
      )}

      {guiV2Enabled && (
        <SystemPromptCard
          title="Live copilot prompt (v2)"
          value={systemPromptV2}
          onChange={(v) => {
            touch();
            setSystemPromptV2(v);
          }}
          isCustom={data.system_prompt_v2_is_custom}
          rows={14}
          onReset={() => {
            setJustSaved(false);
            setSystemPromptV2("");
            save.mutate({ system_prompt_v2: "" });
          }}
          resetDisabled={saving || !data.system_prompt_v2_is_custom}
          help="The system prompt for the v2 copilot brain (stage tracking + call-card extraction + detections). Leave blank to use the built-in default."
          errorSlot={<ErrorNote show={save.isError} />}
        />
      )}

      {/* Live capture & spend guard (different endpoint + its own save button) */}
      <LiveCaptureSection />

      <ModelCard
        label="Live model"
        help="The (fast) model Claude uses for live in-call suggestions."
        options={data.model_options}
        value={model}
        onChange={(v) => {
          touch();
          setModel(v);
        }}
      />

      <SystemPromptCard
        title="Live system prompt"
        value={systemPrompt}
        onChange={(v) => {
          touch();
          setSystemPrompt(v);
        }}
        isCustom={isCustom}
        rows={16}
        onReset={() => {
          setJustSaved(false);
          setSystemPrompt("");
          save.mutate({ system_prompt: "" });
        }}
        resetDisabled={saving || !isCustom}
        help="Leave blank to use the built-in default live-coaching prompt."
        errorSlot={<ErrorNote show={save.isError} />}
      />

      {/* Cadence */}
      <Card className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold">Cadence</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <NumberField
            label="Max nudges / call"
            value={maxPerCall}
            min={0}
            onChange={(v) => {
              touch();
              setMaxPerCall(Math.max(0, Math.round(v)));
            }}
          />
          <NumberField
            label="Min gap (ms)"
            value={minGapMs}
            min={0}
            onChange={(v) => {
              touch();
              setMinGapMs(Math.max(0, Math.round(v)));
            }}
          />
          <NumberField
            label="Nudge TTL (ms)"
            value={ttlMs}
            min={0}
            onChange={(v) => {
              touch();
              setTtlMs(Math.max(0, Math.round(v)));
            }}
          />
          <NumberField
            label="Eval floor (ms)"
            value={evalFloorMs}
            min={0}
            onChange={(v) => {
              touch();
              setEvalFloorMs(Math.max(0, Math.round(v)));
            }}
          />
        </div>
      </Card>

      {/* Reference cards */}
      <Card className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold">Reference cards</h2>
        <p className="text-sm text-muted-foreground">
          Scripts, pricing, and objection-handling cards that surface when the
          customer says a trigger keyword.
        </p>

        <div className="flex flex-col gap-3">
          {cards.map((c, i) => (
            <div key={i} className="rounded-md border p-3">
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 pt-5">
                  <IconButton
                    title="Move up"
                    onClick={() => reorderCard(i, i - 1)}
                    disabled={i === 0}
                  >
                    ↑
                  </IconButton>
                  <IconButton
                    title="Move down"
                    onClick={() => reorderCard(i, i + 1)}
                    disabled={i === cards.length - 1}
                  >
                    ↓
                  </IconButton>
                </div>

                <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_1fr_160px]">
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium">ID</span>
                    <input
                      value={c.id}
                      onChange={(e) => updateCard(i, { id: e.target.value })}
                      placeholder="objection-too-expensive"
                      className="rounded-md border bg-background px-3 py-1.5 font-mono text-xs outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium">Title</span>
                    <input
                      value={c.title}
                      onChange={(e) => updateCard(i, { title: e.target.value })}
                      placeholder="Objection: too expensive"
                      className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>
                  <SelectField
                    label="Type"
                    options={CARD_TYPE_OPTIONS}
                    value={c.type}
                    onChange={(e) =>
                      updateCard(i, {
                        type: e.target.value as LiveReferenceCard["type"],
                      })
                    }
                  />

                  <label className="flex flex-col gap-1 text-sm sm:col-span-3">
                    <span className="font-medium">Triggers (comma-separated)</span>
                    <input
                      value={triggerText[i] ?? ""}
                      onChange={(e) => setCardTriggerText(i, e.target.value)}
                      placeholder="too expensive, cost too much, cheaper"
                      className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>

                  <label className="flex flex-col gap-1 text-sm sm:col-span-3">
                    <span className="font-medium">Body</span>
                    <textarea
                      value={c.body}
                      onChange={(e) => updateCard(i, { body: e.target.value })}
                      rows={3}
                      spellCheck={false}
                      placeholder="What the rep should say…"
                      className="w-full rounded-md border bg-background px-3 py-2 text-xs leading-relaxed outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>
                </div>

                <div className="pt-5">
                  <IconButton title="Delete card" onClick={() => removeCard(i)}>
                    ✕
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
          {cards.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No reference cards yet.
            </p>
          )}
        </div>

        <div>
          <Button variant="ghost" className="text-xs" onClick={addCard}>
            + Add card
          </Button>
        </div>
      </Card>
    </div>
  );
}
